/**
 * Supabase client — single instance for the whole app.
 *
 * ─── WAAROM DEZE KEY HIER MAG STAAN ─────────────────────────────────────────
 *
 * `SUPABASE_PUBLISHABLE_KEY` is bedoeld om in de browser te staan. Iedereen kan
 * hem uitlezen; dat is geen lek. De beveiliging zit volledig in row-level
 * security op de database (zie `supabase/migrations/0001_secure_foundation.sql`).
 *
 * Wat hier NOOIT mag staan:
 *   - de secret key (`sb_secret_…` / `service_role`) — die omzeilt alle RLS
 *   - het database-wachtwoord of een `postgresql://`-connectionstring
 *
 * Een browser kan überhaupt geen directe Postgres-verbinding openen; alle
 * toegang loopt via de REST/auth-endpoints van Supabase.
 */

import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import { error as logError } from '../../utils/logger';
const SUPABASE_URL = 'https://ghhloppktdcvtfdkywvg.supabase.co';
const SUPABASE_PUBLISHABLE_KEY = 'sb_publishable_suT0rGmWvY3YwZ7vLJzJXA_XFADrNed';

/**
 * Eén client-instantie voor de hele app.
 *
 * `persistSession` + `autoRefreshToken` zorgen dat een leerling niet halverwege
 * een toets uitgelogd raakt doordat het access-token verloopt — met een sessie
 * van 20+ minuten is dat een reëel scenario.
 *
 * `detectSessionInUrl` staat aan voor de e-mailconfirmatie- en
 * wachtwoordherstel-links.
 */
export const supabase: SupabaseClient = createClient(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    storageKey: 'meester_djay_auth'
  }
  // ⚠️ GEEN eigen headers hier.
  //
  // Er stond `x-application-name: meester-djay`. Puur cosmetisch — het gaf
  // alleen een naam in de logs — maar het brak registratie volledig.
  //
  // Een niet-standaard header dwingt de browser tot een CORS-preflight en
  // moet daar expliciet worden toegestaan. Voor de REST-endpoints van
  // Supabase gebeurt dat automatisch, maar een Edge Function bepaalt zijn
  // eigen `Access-Control-Allow-Headers`. Stond de header daar niet in, dan
  // blokkeerde de browser de POST vóórdat de functie werd aangeroepen: geen
  // regel in de functie-logs, alleen een CORS-fout in de console.
  //
  // Dat is een slechte ruil — elke Edge Function zou deze header moeten
  // kennen, en vergeten betekent een fout die nergens serverside te zien is.
  // Wat een verzoek doet, hoort in de body te staan, niet in een header die
  // iedere ontvanger apart moet toestaan.
});

/**
 * UUID van de ingelogde gebruiker, of null.
 *
 * `null` betekent hier UITSLUITEND "niet ingelogd". Een fout wordt gelogd en
 * doorgegooid, want die twee gevallen mogen niet op elkaar lijken: een
 * netwerkstoring die als "uitgelogd" wordt gelezen, stuurt iemand terug naar
 * het inlogscherm terwijl zijn sessie prima geldig is — en op een gedeeld
 * klaslokaalapparaat is dat halverwege een toets.
 */
export async function getCurrentUserId(): Promise<string | null> {
  const {
    data,
    error
  } = await supabase.auth.getUser();
  if (error) {
    // `AuthSessionMissingError` is geen storing maar het normale antwoord op
    // "er is niemand ingelogd". Alleen dát geval mag stil `null` opleveren.
    if (error.name === 'AuthSessionMissingError') return null;
    logError('getCurrentUserId: sessie kon niet worden opgehaald', error);
    throw error;
  }
  return data.user?.id ?? null;
}

/**
 * Rol + profiel van de ingelogde gebruiker.
 *
 * De rol komt uit `public.profiles`, NIET uit JWT-metadata: metadata is door de
 * client beïnvloedbaar, een profielrij niet (zie de `profiles_update_self`
 * policy, die rolwijziging blokkeert).
 */
export interface CurrentProfile {
  id: string;
  role: 'learner' | 'parent' | 'teacher' | 'admin';
  displayName: string;
  grade: number | null;
}
export async function getCurrentProfile(): Promise<CurrentProfile | null> {
  const userId = await getCurrentUserId();
  if (!userId) return null;
  const {
    data,
    error
  } = await supabase.from('profiles').select('id, role, display_name, grade').eq('id', userId).single();
  if (error) {
    // PGRST116 = "geen rij gevonden". Dat is een echte toestand (ingelogd,
    // maar nog geen profielrij) en geen storing. Al het andere is dat wel.
    if (error.code === 'PGRST116') return null;
    logError('getCurrentProfile: profiel kon niet worden opgehaald', error);
    throw error;
  }
  if (!data) return null;
  return {
    id: data.id as string,
    role: data.role as CurrentProfile['role'],
    displayName: data.display_name as string,
    grade: data.grade as number | null ?? null
  };
}